/**
 * MobileCharities — Charities · Donations · Volunteers directory.
 *
 * Top segment picks the primary lens (Charities/Donations vs.
 * Volunteers). A second floating pill row lets the user drill into
 * Charities / Donations / Volunteers with a distinct emphasis.
 * Bottom-of-page CTAs let anyone Add/Suggest a Charity/Donation or
 * Become a Volunteer.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, HeartHandshake } from "lucide-react";
import { CATEGORY_TILES, CategoryTile } from "./MobileHome";
import { categoryIllustration } from "@/config/categoryIllustrations";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

const PRIMARY = [
  { key: "charities-donations", label: "Charities/Donations" },
  { key: "volunteers",          label: "Volunteers" },
];

const FLOAT_TABS = [
  { key: "charities",  label: "Charities" },
  { key: "donations",  label: "Donations" },
  { key: "volunteers", label: "Volunteers" },
];

export default function MobileCharities() {
  const [params, setParams] = useSearchParams();
  const primary = params.get("p") || "charities-donations";
  const float   = params.get("f") || "charities";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (float === "volunteers") {
      // Volunteers lens: stub data — will be replaced when the
      // volunteers collection ships.
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API}/api/food-businesses`, {
      params: {
        business_type: "food_bank,food_pantry,food_charity,soup_kitchen",
        limit: 30,
      },
    })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [float]);

  const setPrimary = (k) => { const p = new URLSearchParams(params); p.set("p", k); setParams(p, { replace: true }); };
  const setFloat = (k) => { const p = new URLSearchParams(params); p.set("f", k); setParams(p, { replace: true }); };

  return (
    <div className="pb-24" data-testid="mobile-charities">
      {/* Category tiles with Charities active */}
      <div
        className="grid grid-cols-4 gap-2 px-3 pt-3 pb-4"
        style={{ background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)" }}
      >
        {CATEGORY_TILES.map((t) => (
          <CategoryTile key={t.key} tile={t} active={t.key === "charities"} />
        ))}
      </div>

      {/* Primary segmented control */}
      <div className="px-4 mt-3 flex gap-2" data-testid="charities-primary-tabs">
        {PRIMARY.map((t) => {
          const active = t.key === primary;
          return (
            <button
              key={t.key}
              onClick={() => setPrimary(t.key)}
              data-testid={`charities-primary-${t.key}`}
              className="flex-1 py-2.5 rounded-full text-sm font-semibold"
              style={{
                background: active ? FP_PURPLE : "#fff",
                color: active ? "#fff" : "#1c1c1c",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Floating sub-tabs (Charities · Donations · Volunteers) */}
      <div
        className="sticky top-[60px] z-20 mt-4 mx-4 rounded-full bg-white/95 backdrop-blur border border-gray-200 shadow-lg p-1 flex gap-1"
        data-testid="charities-float-tabs"
      >
        {FLOAT_TABS.map((t) => {
          const active = t.key === float;
          return (
            <button
              key={t.key}
              onClick={() => setFloat(t.key)}
              data-testid={`charities-float-${t.key}`}
              className="flex-1 py-2 rounded-full text-sm font-bold"
              style={{
                background: active ? FP_GREEN : "transparent",
                color: active ? "#fff" : FP_PURPLE,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Results */}
      <div className="mt-4 px-4 space-y-3" data-testid="charities-list">
        {loading ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 opacity-60 text-sm">
            {float === "volunteers"
              ? "Sign up as a volunteer below to get matched with local charities."
              : "No charities in your area yet — help seed the directory below."}
          </div>
        ) : (
          items.map((b) => {
            const cover = (b.images || [])[0] || categoryIllustration(b.business_type, b.slug, {
              subType: b.sub_type, cuisines: b.cuisines, name: b.name,
            });
            return (
              <Link
                to={`/food-pulse/business/${b.slug}`}
                key={b.slug}
                data-testid={`charities-item-${b.slug}`}
                className="flex items-center gap-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-3"
              >
                {cover && <img src={cover} alt={b.name} loading="lazy" className="w-16 h-16 object-cover rounded-lg" />}
                <div className="flex-1 min-w-0">
                  <div className="text-base font-bold truncate">{b.name}</div>
                  {b.phone && <div className="text-xs text-gray-600">{b.phone}</div>}
                  {b.address && <div className="text-xs mt-1 text-gray-600 line-clamp-2">{b.address}</div>}
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Bottom CTAs */}
      <div className="fixed bottom-16 left-0 right-0 px-4 py-3 bg-white/95 backdrop-blur border-t z-20">
        <div className="flex gap-2">
          <button
            data-testid="charities-add-cta"
            className="flex-1 py-3 rounded-2xl text-white text-sm font-semibold flex items-center justify-center gap-2 shadow"
            style={{ background: FP_PURPLE }}
          >
            <Plus className="w-4 h-4" /> Add/Suggest a Charity/Donation
          </button>
          <button
            data-testid="charities-volunteer-cta"
            className="py-3 px-4 rounded-2xl text-white text-sm font-semibold flex items-center justify-center gap-2 shadow"
            style={{ background: FP_GREEN }}
          >
            <HeartHandshake className="w-4 h-4" /> Become Volunteer
          </button>
        </div>
      </div>
    </div>
  );
}
